import { useCallback, useEffect, useRef, useState } from 'react';
import { useToast } from '@/hooks/use-toast';
import { invokeFunction, supabase } from '@/integrations/supabase/client';
import { API_BASE_URL, getAuthHeaders } from '@/utils/apiConfig';
import { formatFivetranSyncDetail, isRescheduledSyncState } from '@/utils/fivetranSyncStatus';
import { isConnectionUnavailableBody } from '@/utils/connectionSettingsUtils';
import { saveOrganisationConnections } from '@/utils/organization/saveOrganization';
import {
  ConnectionRecord,
  ConnectionsMap,
  ConnectionsOwner,
  connectionEntries,
  connectorIdOf,
  isCsvConnectionRecord,
  isPlaidConnectionRecord,
  parseSnapshotDateFromInventoryImportId,
} from '@/shared/merchantFeeds';
import type { LastSync, LastSyncsByConnector } from '@/shared/types/connectionSync';

export type { ConnectionRecord, ConnectionsMap, ConnectionsOwner };

// A merchant's data connections: live Fivetran status, test-clone inheritance,
// removing a connection and removing a single CSV file. Extracted unchanged
// from the Overview tab's Data Connections card, which FIG-321 replaces with
// Settings › Pricing Data In › Data Feeds (REQ-054).

interface LiveStatus {
  setup_state?: string;
  sync_state?: string;
  paused?: boolean;
  loading: boolean;
  rescheduled_for?: string | null;
  sync_tasks?: Array<{ message?: string; details?: string }>;
  sync_warnings?: Array<{ message?: string; details?: string }>;
  status_reason?: unknown;
  unavailableInEnvironment?: boolean;
}

export interface StatusIndicator {
  color: string;
  label: string;
  detail?: string | null;
  rescheduledFor?: string | null;
}

/** Matches import-inventory-csv-v3 `source` labels for variance rows. */
function inventorySourceFromEcommerceSubtype(connection: { ecommerce_subtype?: string } | undefined): string | undefined {
  const s = String(connection?.ecommerce_subtype || '').toLowerCase();
  if (s === 'shopify') return 'shopify_csv_import';
  if (s === 'amazon') return 'amazon_csv_import';
  return undefined;
}

export type MerchantConnectionsApi = ReturnType<typeof useMerchantConnections>;

export interface UseMerchantConnectionsOptions {
  /** Fetch live status and inheritance only while true (e.g. while a group showing connections is open). */
  enabled?: boolean;
}

export function useMerchantConnections(
  owner: ConnectionsOwner,
  onChanged?: () => void,
  { enabled = true }: UseMerchantConnectionsOptions = {},
) {
  const { toast } = useToast();
  const [liveStatuses, setLiveStatuses] = useState<Record<string, LiveStatus>>({});
  // Last successful sync per row (FIG-345 UC-231), keyed by connectorIdOf.
  const [lastSyncs, setLastSyncs] = useState<Record<string, LastSync>>({});
  const [removingId, setRemovingId] = useState<string | null>(null);
  // After a removal the merchant is refreshed in the background. Until the
  // new connections arrive, another removal would start from the old list and
  // could write the removed entry back, so removals stay locked meanwhile.
  const [awaitingRefresh, setAwaitingRefresh] = useState(false);
  const connectionsBeforeRefresh = useRef<unknown>(null);
  const busy = removingId !== null || awaitingRefresh;

  useEffect(() => {
    if (awaitingRefresh && owner?.connections !== connectionsBeforeRefresh.current) setAwaitingRefresh(false);
  }, [awaitingRefresh, owner?.connections]);

  // Fallback so a failed refresh cannot lock removals for good.
  useEffect(() => {
    if (!awaitingRefresh) return;
    const timer = setTimeout(() => setAwaitingRefresh(false), 15_000);
    return () => clearTimeout(timer);
  }, [awaitingRefresh]);

  const waitForRefresh = () => {
    connectionsBeforeRefresh.current = owner?.connections;
    setAwaitingRefresh(true);
  };
  const [sourceConnectionKeys, setSourceConnectionKeys] = useState<Set<string> | null>(null);
  const [sourceImportIds, setSourceImportIds] = useState<Set<string> | null>(null);

  // For test clones, fetch the source org's connection keys and csvImport ids.
  useEffect(() => {
    if (!enabled) return;
    if (!owner?.is_test_clone || !owner?.source_organisation_id) {
      setSourceConnectionKeys(null);
      setSourceImportIds(null);
      return;
    }

    let cancelled = false;
    const fetchSourceConnections = async () => {
      const { data } = await supabase
        .from('organisations')
        .select('connections')
        .eq('id', owner.source_organisation_id as string)
        .single();

      if (cancelled) return;
      if (data?.connections && typeof data.connections === 'object') {
        const conns = data.connections as unknown as Record<string, ConnectionRecord>;
        setSourceConnectionKeys(new Set(Object.keys(conns)));
        const importIds = new Set<string>();
        Object.values(conns).forEach((conn) => {
          if (Array.isArray(conn.csvImports)) {
            conn.csvImports.forEach((imp) => {
              if (imp.id) importIds.add(imp.id);
            });
          }
        });
        setSourceImportIds(importIds);
      }
    };

    fetchSourceConnections();
    return () => {
      cancelled = true;
    };
  }, [enabled, owner?.is_test_clone, owner?.source_organisation_id]);

  const isInheritedConnection = useCallback((key: string): boolean => {
    if (!owner?.is_test_clone || !sourceConnectionKeys) return false;
    return sourceConnectionKeys.has(key);
  }, [owner?.is_test_clone, sourceConnectionKeys]);

  const isInheritedImport = useCallback((importId: string): boolean => {
    if (!owner?.is_test_clone || !sourceImportIds) return false;
    return sourceImportIds.has(importId);
  }, [owner?.is_test_clone, sourceImportIds]);

  // Fetch real-time Fivetran status for non-CSV connections.
  useEffect(() => {
    if (!enabled) return;
    const entries = connectionEntries(owner?.connections);
    const fivetranConnections = entries.filter(([, value]) =>
      !isCsvConnectionRecord(value) && value.service !== 'file' && value.type !== 'plaid');

    if (fivetranConnections.length === 0) return;

    // Responses from an earlier run (e.g. before a group was closed and
    // reopened) must not overwrite newer ones.
    let cancelled = false;
    const initialStatuses: Record<string, LiveStatus> = {};
    fivetranConnections.forEach(([key, value]) => {
      initialStatuses[connectorIdOf(key, value)] = { loading: true };
    });
    setLiveStatuses(initialStatuses);

    fivetranConnections.forEach(async ([key, value]) => {
      const connectorId = connectorIdOf(key, value);
      try {
        const res = await fetch(
          `${API_BASE_URL}/manage-connection-settings?action=details&connectorId=${encodeURIComponent(connectorId)}`,
          { headers: await getAuthHeaders() }
        );
        if (res.ok) {
          const data = await res.json();
          if (cancelled) return;
          setLiveStatuses((prev) => ({
            ...prev,
            [connectorId]: {
              setup_state: data.setup_state,
              sync_state: data.sync_state,
              paused: data.paused,
              loading: false,
              rescheduled_for: data.rescheduled_for ?? null,
              sync_tasks: Array.isArray(data.sync_tasks) ? data.sync_tasks : [],
              sync_warnings: Array.isArray(data.sync_warnings) ? data.sync_warnings : [],
              status_reason: data.status_reason,
            },
          }));
        } else {
          const body = await res.text();
          if (cancelled) return;
          setLiveStatuses((prev) => ({
            ...prev,
            [connectorId]: { loading: false, unavailableInEnvironment: isConnectionUnavailableBody(body) },
          }));
        }
      } catch (err) {
        console.error(`Failed to fetch status for connector ${connectorId}:`, err);
        if (cancelled) return;
        setLiveStatuses((prev) => ({ ...prev, [connectorId]: { loading: false } }));
      }
    });
    return () => {
      cancelled = true;
    };
  }, [enabled, owner?.connections]);

  // Last successful sync of every API connection (FIG-345 UC-231). Fetched
  // once per open, like the live status; rows show nothing until it arrives.
  useEffect(() => {
    if (!enabled || !owner?.id) return;
    const requests = connectionEntries(owner.connections)
      .filter(([, value]) => !isCsvConnectionRecord(value) && value.service !== 'file')
      .map(([key, value]) => {
        const rowId = connectorIdOf(key, value);
        const plaid = isPlaidConnectionRecord(value);
        // Plaid webhooks are stored by item id, which is the connection's connectionId.
        return { rowId, id: plaid ? value.connectionId || rowId : rowId, kind: plaid ? ('plaid' as const) : ('fivetran' as const) };
      });
    if (requests.length === 0) return;

    let cancelled = false;
    invokeFunction<{ syncs?: LastSyncsByConnector }>(supabase, 'connection-sync-status-v1', {
      body: { organisationId: owner.id, connectors: requests.map(({ id, kind }) => ({ id, kind })) },
    })
      .then(({ data, error }) => {
        if (cancelled || error || !data?.syncs) return;
        const next: Record<string, LastSync> = {};
        for (const request of requests) next[request.rowId] = data.syncs[request.id] ?? { at: null, source: null };
        setLastSyncs(next);
      })
      .catch((err: unknown) => console.error('Failed to fetch last sync times:', err));
    return () => {
      cancelled = true;
    };
  }, [enabled, owner?.id, owner?.connections]);

  /** Last successful sync for a row, or null while it is loading or could not be read. */
  const getLastSync = (connectorId: string): LastSync | null => lastSyncs[connectorId] ?? null;

  const getStatusIndicator = (value: ConnectionRecord, connectorId: string, isCsv: boolean): StatusIndicator => {
    if (isCsv) return { color: 'bg-green-500', label: 'Connected' };

    const live = liveStatuses[connectorId];
    if (live?.loading) return { color: 'bg-gray-300 animate-pulse', label: 'Checking...' };

    // UC-168: the stored Fivetran ID was issued by another account, so this
    // connection cannot be resolved here. Say so rather than leaving it blank.
    if (live?.unavailableInEnvironment) {
      return {
        color: 'bg-gray-400',
        label: 'Unavailable in this environment',
        detail: 'This connection was created under a different Fivetran account, so its live status cannot be read here.',
      };
    }

    if (live?.setup_state) {
      if (live.setup_state === 'broken') return { color: 'bg-red-500', label: 'Broken' };
      if (live.setup_state === 'incomplete') return { color: 'bg-yellow-500', label: 'Incomplete' };
      if (live.paused) return { color: 'bg-yellow-500', label: 'Paused' };
      if (isRescheduledSyncState(live.sync_state)) {
        const detail = formatFivetranSyncDetail({
          syncState: live.sync_state,
          rescheduledFor: live.rescheduled_for,
          tasks: live.sync_tasks,
          warnings: live.sync_warnings,
          statusReason: live.status_reason,
        });
        return { color: 'bg-blue-500', label: 'Syncing (rescheduled)', detail, rescheduledFor: live.rescheduled_for ?? null };
      }
      if (live.sync_state === 'syncing') return { color: 'bg-blue-500', label: 'Syncing' };
      return { color: 'bg-green-500', label: 'Connected' };
    }

    // Plaid connections: check stored status directly (no live Fivetran poll)
    if (isPlaidConnectionRecord(value)) {
      // Tokens are kept server-side (FIG-269); the stored entry only says whether one exists.
      const hasToken = value.metadata?.has_access_token === true || !!value.metadata?.access_token;
      const s = typeof value.status === 'string' ? value.status : undefined;
      if (!hasToken) {
        return {
          color: 'bg-orange-400',
          label: 'Connection data lost',
          detail: 'Bank connection data lost. Remove this bank and connect it again.',
        };
      }
      if (s === 'expiring') {
        return {
          color: 'bg-orange-400',
          label: 'Needs Re-authentication',
          detail: 'Bank connection credentials have expired. Re-authenticate to keep data syncing.',
        };
      }
      if (s === 'error') {
        return {
          color: 'bg-red-500',
          label: 'Authentication Error',
          detail: value.metadata?.error?.error_message || 'Plaid reported an error. Re-authenticate to restore the connection.',
        };
      }
      if (s === 'disconnected') {
        return { color: 'bg-red-500', label: 'Disconnected', detail: 'The user revoked bank access. A new connection must be authorised.' };
      }
      const isActive = s === 'active' || s === 'connected';
      return { color: isActive ? 'bg-green-500' : 'bg-yellow-500', label: isActive ? 'Connected' : 'Incomplete' };
    }

    const isConnected = value.status === 'connected';
    return { color: isConnected ? 'bg-green-500' : 'bg-yellow-500', label: isConnected ? 'Connected' : 'Incomplete' };
  };

  const deleteConnection = async (key: string, value: ConnectionRecord) => {
    if (busy) return;
    const connectorId = connectorIdOf(key, value);
    setRemovingId(key);
    try {
      const { error } = await invokeFunction(supabase, 'remove-data-connector', {
        body: {
          connectorId,
          connectionId: value.connectionId || value.id || key,
          organisationId: owner.id,
          deleteDatabase: true,
        },
      });
      if (error) throw error;
      toast({ title: 'Connection removed', description: `${value.name || key} has been removed and its database deleted.` });
      waitForRefresh();
      onChanged?.();
    } catch (err: unknown) {
      console.error('Failed to remove connection:', err);
      toast({ title: 'Error', description: err instanceof Error ? err.message : 'Failed to remove connection', variant: 'destructive' });
    } finally {
      setRemovingId(null);
    }
  };

  const deleteCsvImport = async (connectionKey: string, importId: string, fileName: string) => {
    if (busy) return;
    setRemovingId(importId);
    try {
      const sourceConnections = owner.connections as ConnectionsMap;
      const connection = (sourceConnections as Record<string, ConnectionRecord>)[connectionKey];
      const csvImports = connection.csvImports || [];
      const importToDelete = csvImports.find((imp) => imp.id === importId);
      const updatedImports = csvImports.filter((imp) => imp.id !== importId);

      let updatedConnections: ConnectionsMap;
      if (Array.isArray(sourceConnections)) {
        // Preserve array shape: connectionKey is the string index (Object.entries on an array yields "0","1",...).
        const idx = Number(connectionKey);
        updatedConnections = updatedImports.length === 0
          ? [...sourceConnections.slice(0, idx), ...sourceConnections.slice(idx + 1)]
          : sourceConnections.map((c, i) => (i === idx ? { ...connection, csvImports: updatedImports } : c));
      } else if (updatedImports.length === 0) {
        const copy = { ...sourceConnections };
        delete copy[connectionKey];
        updatedConnections = copy;
      } else {
        updatedConnections = { ...sourceConnections, [connectionKey]: { ...connection, csvImports: updatedImports } };
      }

      // Saved through organisations-manager-v3 so bank tokens aren't dropped (FIG-269).
      const { error } = await saveOrganisationConnections(owner.id, updatedConnections);
      if (error) throw error;

      const parentConnectionId = (connection.connectionId || connection.id || connectionKey) as string;
      const snapshotConn = importToDelete?.snapshot_connection_id;
      const snapshotIdForCleanup = updatedImports.length === 0 ? parentConnectionId : snapshotConn;
      const jobId = importToDelete?.csv_import_job_id;
      const snapDate = importToDelete?.snapshot_date || parseSnapshotDateFromInventoryImportId(importId);
      const invSource = importToDelete?.inventory_source || inventorySourceFromEcommerceSubtype(connection);
      const hasVarianceKeys = Boolean(snapDate && invSource);

      if (snapshotIdForCleanup || jobId || hasVarianceKeys) {
        try {
          const { data: cleanupData, error: cleanupError } = await invokeFunction<{ success?: boolean; error?: string }>(
            supabase,
            'cleanup-csv-import-artifacts-v1',
            {
              body: {
                organisation_id: owner.id,
                ...(snapshotIdForCleanup && { snapshot_connection_id: snapshotIdForCleanup }),
                ...(hasVarianceKeys && { snapshot_date: snapDate, inventory_source: invSource }),
                ...(jobId && { csv_import_job_id: jobId }),
              },
            },
          );
          if (cleanupError) {
            console.warn('Non-blocking: cleanup-csv-import-artifacts-v1 failed:', cleanupError);
          } else if (cleanupData && typeof cleanupData === 'object' && 'success' in cleanupData && !cleanupData.success) {
            console.warn('Non-blocking: cleanup-csv-import-artifacts-v1:', cleanupData);
          }
        } catch (cleanupErr) {
          console.warn('Non-blocking: cleanup-csv-import-artifacts-v1 error:', cleanupErr);
        }
      }

      toast({ title: 'Import removed', description: `${fileName} has been removed.` });
      waitForRefresh();
      onChanged?.();
    } catch (err: unknown) {
      console.error('Failed to remove CSV import:', err);
      toast({ title: 'Error', description: err instanceof Error ? err.message : 'Failed to remove import', variant: 'destructive' });
    } finally {
      setRemovingId(null);
    }
  };

  return {
    getStatusIndicator,
    getLastSync,
    isInheritedConnection,
    isInheritedImport,
    deleteConnection,
    deleteCsvImport,
    removingId,
    /** A removal is running or its refresh has not arrived yet: removal controls are locked. */
    busy,
  };
}
